import React from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import PanelHeader from "components/panelheader/panelHeader";
import { FormGroup, Input, Button, Card, CardBody, Row, Col } from "reactstrap";
import Select from "react-select";
import { getUser, saveUser } from "services/userService";
import { getProfilesSelect } from "services/profilesService";
import { UserContext } from "contexts/userContext";
import { toastBigError } from "components/utils/toasts";
import Joi from "joi-browser";
import { usePageLoading } from "hooks/usePageLoading";

const UsuariosForm = (props) => {
  const doneLoading = usePageLoading(props);
  let { id } = useParams();
  let location = useLocation();
  let navigate = useNavigate();
  const user = React.useContext(UserContext);
  const validateForm = true;
  const [data, setData] = React.useState({ _id: id ? id : null, nombre: "", apellido: "", email: "", password: "", profileId: "" });
  const [errors, setErrors] = React.useState([]);
  const [profiles, setProfiles] = React.useState([]);
  const [action, setAction] = React.useState(location.state?.action ? location.state.action : "");
  const readOnly = action === "C";

  const schema = {
    _id: Joi.string(),
    nombre: Joi.string()
      .label("Nombre")
      .required()
      .error((errors) => errors.map(() => ({ message: "Debe ingresar un nombre" }))),
    apellido: Joi.string()
      .label("Apellido")
      .allow("")
      .optional(),
    email: Joi.string()
      .label("Email")
      .email()
      .required()
      .error((errors) =>
        errors.map((error) => {
          switch (error.type) {
            case "string.email":
              return { message: "Debe ingresar un email válido" };
            default:
              return { message: "Debe ingresar un email" };
          }
        })
      ),
    password: Joi.string().label("Contraseña").allow("").optional(),
    profileId: Joi.string()
      .label("Perfil")
      .required()
      .error((errors) => errors.map(() => ({ message: "Debe seleccionar un perfil" }))),
  };

  const fetchData = async (id) => {
    const record = await getUser(id);
    mapToViewModel(record);
  };

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    // Esta pantalla trae dos cosas (perfiles y usuario): el loader se libera
    // recién cuando terminaron todas.
    const pendientes = [fetchProfiles()];
    const id = data._id;
    if (id && !id.includes("nuev")) {
      pendientes.push(fetchData(id));
    } else {
      const cacheData = { ...data };
      delete cacheData._id;
      setData(cacheData);
      setAction("A");
    }
    Promise.allSettled(pendientes).then(doneLoading);
  }, []);

  const fetchProfiles = async () => {
    const records = await getProfilesSelect();
    if (Array.isArray(records)) setProfiles(records);
  };

  const mapToViewModel = (usuario) => {
    setData({
      _id: usuario._id,
      nombre: usuario.nombre || "",
      apellido: usuario.apellido || "",
      email: usuario.email || "",
      password: "",
      profileId: usuario.profile ? { value: usuario.profile._id, label: usuario.profile.descripcion } : "",
    });
  };

  const handleChange = (event) => {
    const input = event.currentTarget;
    const inputMode = event.target.inputMode;
    const cacheErrors = { ...errors };
    const cacheData = { ...data };
    if (validateForm) {
      const errorMessage = validateProperty(input, inputMode);
      if (errorMessage) {
        cacheErrors[input.name] = errorMessage;
      } else delete cacheErrors[input.name];
    } else delete cacheErrors[input.name];
    cacheData[input.name] = input.value;
    if (input.type === "checkbox") cacheData[input.name] = input.checked;
    setData(cacheData);
    setErrors(cacheErrors);
  };

  const handleSelect = (option) => {
    const cacheErrors = { ...errors };
    const cacheData = { ...data };
    cacheData.profileId = option ? { value: option.value, label: option.label } : "";
    delete cacheErrors.profileId;
    setData(cacheData);
    setErrors(cacheErrors);
  };

  const validateRules = () => {
    return null;
  };

  const validate = () => {
    let errors = {};
    const options = { abortEarly: false };
    if (validateForm) {
      Object.keys(data).map(function (key, index) {
        if (typeof data[key] === "object" && data[key] && data[key].value && typeof data[key].value === "string") {
          data[key] = data[key].value;
        }
      });
      let { error } = Joi.validate(data, schema, options);
      if (!error) {
        error = validateRules();
        if (!error) {
          Object.keys(data).map(function (key, index) {
            if (typeof data[key] === "object" && data[key] && data[key].value) data[key] = data[key].value;
          });
          return null;
        }
        error.map((error) => {
          toastBigError(error.message, 2500);
          return (errors[error.path] = error.message);
        });
        return errors;
      } else {
        error.details.map((validationError) => {
          return (errors[validationError.path[0]] = validationError.message);
        });
        return errors;
      }
    } else return null;
  };

  const validateProperty = (input, inputMode) => {
    let { name, value } = input;
    const object = { [name]: value };
    const checkSchema = { [name]: schema[name] };
    const { error } = Joi.validate(object, checkSchema);
    return error ? error.details[0].message : null;
  };

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    const checkErrors = validate();
    if (checkErrors) {
      setErrors(checkErrors);
      return;
    }
    doSubmit();
  };

  const doSubmit = async () => {
    const payload = { ...data };
    if (!payload.password) delete payload.password;
    if (!payload._id) {
      payload.parentId = user && user.empresa ? user.empresa._id || user.empresa : null;
    }
    await saveUser(payload, payload.parentId);
    navigate(-1);
  };

  const goBack = () => {
    navigate(-1);
  };

  return (
    <>
      <PanelHeader size="sm" />
      <div className="content">
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <Row>
                  <Col md="6">
                    <FormGroup className={`has-label ` + (errors["nombre"] ? "has-danger" : "")}>
                      <label>Nombre</label>
                      <Input id="nombre" name="nombre" autoComplete="off" disabled={readOnly} value={data.nombre} onChange={(e) => handleChange(e)} />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup className={`has-label ` + (errors["apellido"] ? "has-danger" : "")}>
                      <label>Apellido</label>
                      <Input id="apellido" name="apellido" autoComplete="off" disabled={readOnly} value={data.apellido} onChange={(e) => handleChange(e)} />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup className={`has-label ` + (errors["email"] ? "has-danger" : "")}>
                  <label>Email</label>
                  <Input id="email" name="email" type="email" autoComplete="off" disabled={readOnly} value={data.email} onChange={(e) => handleChange(e)} />
                </FormGroup>
                <FormGroup className={`has-label ` + (errors["password"] ? "has-danger" : "")}>
                  <label>Contraseña {data._id ? "(dejar en blanco para no cambiarla)" : ""}</label>
                  <Input id="password" name="password" type="password" autoComplete="new-password" disabled={readOnly} value={data.password} onChange={(e) => handleChange(e)} />
                </FormGroup>
                <FormGroup className={`has-label ` + (errors["profileId"] ? "has-danger" : "")}>
                  <label>Perfil</label>
                  <Select
                    className="react-select perfil-select"
                    classNamePrefix="react-select"
                    placeholder="Seleccione un perfil"
                    name="profileId"
                    isDisabled={readOnly}
                    value={data.profileId || null}
                    onChange={handleSelect}
                    options={profiles}
                  />
                </FormGroup>
                <Row>
                  <Col md="12">
                    <Button className="pull-right btn-cancelar" onClick={goBack}>
                      {readOnly ? "Volver" : "Cancelar"}
                    </Button>
                    {!readOnly && (
                      <Button className="pull-right btn-arhock" onClick={handleSubmit}>
                        Aceptar
                      </Button>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default UsuariosForm;
